/**
 * Definizione dell' interfaccia e della classe CentroDiCostoRecord, la struttura dati che rappresenta la tabella aziendale polivalente.
 * Questo modello di dati viene usati infatti per più scopi, può rappresentare il centro di costo, la qualifica, la mansione, etc.
 * Il campo idTipo ne determina appunto la tipologia:
 * ID_TIPO ITEM_NAME            DESCRIZIONE
 * ---------------------------------------------------------------------
 * 0       COD_CDIC             Centri di costo
 * 1       QUALIFICA            Qualifiche
 * 3       ID_INAIL             Inail
 * 4       ID_INPS              Inps
 * 10      STATUS               Status lavoratore/azienda
 * 12      PROF_RETRIBUTIVO     Profilo retributivo
 * 13      TIPO_CONTRATTO       Tipologia contratto
 * 14      CAUSALE_TDET         Causale TDET
 * 15      ISTITUTI_CCNL        Istituti contrattuali
 * 18      MANSIONE             Mansioni
 * 19      RUOLO                Ruoli
 * 21      GRUPPO01             Gruppo 1
 * 22      GRUPPO02             Gruppo 2
 * 23      GRUPPO03             Gruppo 3
 * 24      GRUPPO04             Gruppo 4
 * 25      GRUPPO05             Gruppo 5
 * 26      GRUPPO06             Gruppo 6
 * 27      GRUPPO07             Gruppo 7
 * 28      GRUPPO08             Gruppo 8
 * 29      GRUPPO09             Gruppo 9
 * 30      GRUPPO10             Gruppo 10
 * 31      GRUPPO11             Gruppo 11
 * 32      GRUPPO12             Gruppo 12
 * 40      SCADENZE             Tipologie scadenze
 * 41      ACCUMULO             Accumulo giustificativi presenze
 * 50      ELEMENTI_RML         Elementi RML
 * 99      AG_INTER             Agenzie interinali (TEMP)
 * Per ogni tipologia i campi generici altro1-altro7 assumono particolari significati, es. per la qualifica il campo altro2 rappresenta la "Dicitura compenso/RAL",
 * per la posizione previdenziale (INPS), lo stesso campo rappresenta invece la provincia della sede Inps di competenza territoriale.
 * Ciascuna azienda può avere un proprio impianto tabellare, ogni singolo valore tabellare può essere poi assegnato a ciascun lavoratore.
 */
import {ObjectFactory} from "@commons/types";
import {TabellaAzienda} from "@commons/mapping/TabellaAzienda";
import {Giustificativi} from "@commons/mapping/Giustificativi";
import {StatusGiustificativoRecord} from "@commons/mapping/StatusGiustificativoRecord";
import {FPNetDate} from "@commons/types";
import {merge} from "lodash";
import {DateTime} from "luxon";
import {DateTimeUtils, ValidDtRange} from "@commons/utils/DateTimeUtils";

export interface CentroDiCostoRecord extends TabellaAzienda, ValidDtRange {
    descrizione2: string | null, // max 40 caratteri
    altro1: string | null, // max 40 caratteri
    altro2: string | null, // max 40 caratteri
    altro3: string | null, // max 10 caratteri
    altro4: string | null, // max 60 caratteri
    altro5: number | null, // intero
    altro6: number | null, // intero
    altro7: number | null, // intero
    livelloEdit: number | null,
    countGiustificativi: number | null,
    countCDICRelazionati: number | null,
    colore: string | null, // max 7 caratteri, es. #ffccee

    // Le seguenti 2 proprietà sembrano contenere la stessa cosa...
    giustificativi: StatusGiustificativoRecord[] | Giustificativi[];  // array con giustificativi
    statusGiustificativi: StatusGiustificativoRecord[];               // array con giustificativi relazionati ad esempio all'istituto contrattuale, istanza di StatusGiustificativoRecord[]

    validitaInizio: FPNetDate,
    validitaFine: FPNetDate,
}

export class CentroDiCostoRecord {
    static factory: ObjectFactory<CentroDiCostoRecord> = (json?) => {

        return merge<CentroDiCostoRecord, Partial<CentroDiCostoRecord> | undefined>({
            idAzienda: 0,
            idTipo: 0,
            id: null,
            descrizione: null,
            descrizione2: null,
            altro1: null,
            altro2: null,
            altro3: null,
            altro4: null,
            altro5: null,
            altro6: null,
            altro7: null,
            livelloEdit: null,
            countGiustificativi: null,
            countCDICRelazionati: null,
            colore: null,
            giustificativi: [],
            statusGiustificativi: [],
            URI: null,
            validitaInizio: null,
            validitaFine: null,
        }, json);

    }

    static getDescrizione = (cdic: CentroDiCostoRecord) => {
        return cdic ? cdic.descrizione + ' [#' + cdic.id + ']' : '???';
    }
    static isValid = (cdic: CentroDiCostoRecord, dtRiferimento: DateTime = DateTime.now()) => {
        return DateTimeUtils.isBetween(cdic, dtRiferimento);
    }
}

// Definisco un type per ciascuna tabella rappresentate da CentroDiCostoRecord
export type ProfRetributivoRecord = CentroDiCostoRecord; // per idTipo 12
export type ValoreGruppoRecord = CentroDiCostoRecord; // per idTipo 21-32
export type QualificaRecord = CentroDiCostoRecord; // per idTipo 1
export type TabellaElementiRMLRecord = CentroDiCostoRecord; // per idTipo 50